/** The access question an app's file call raises, and the ⋯ "File access…" setting. */
import {useState, type ReactNode} from "react"

import {type AppAccess} from "@agenta/entities/drive"
import {useLocalize, type TranslationKeys} from "@agenta/i18n"
import {
    Button,
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    RadioGroup,
    RadioGroupItem,
} from "@agenta/ui/ui"

/** How a stored level reads in the UI; null is "never answered". */
export const accessLabel = (t: ReturnType<typeof useLocalize>, level: AppAccess | null): string =>
    t(
        level === "read-write"
            ? "com_entity_drive_access_read_write"
            : level === "read"
              ? "com_entity_drive_access_read"
              : level === "none"
                ? "com_ui_none"
                : "com_entity_drive_access_not_set",
    )

const FolderCode = ({dir}: {dir: string}) => (
    <code className="rounded bg-colorFillSecondary px-1 py-0.5 text-[11px] text-colorText">
        {dir || "/"}
    </code>
)

// Splits a translated sentence at its `{{dir}}` slot so the folder renders as code in place.
const DIR_SLOT = "\u0000dir\u0000"
const withFolder = (text: string, dir: string): ReactNode => {
    const [before, after = ""] = text.split(DIR_SLOT)
    return (
        <>
            {before}
            <FolderCode dir={dir} />
            {after}
        </>
    )
}

/** The dialog frame both share: page-modal, or confined to `container` with the page live. */
const AccessDialog = ({
    open,
    container,
    onDismiss,
    showCloseButton,
    children,
}: {
    open: boolean
    container?: HTMLElement | null
    onDismiss: () => void
    showCloseButton: boolean
    children: ReactNode
}) => (
    <Dialog
        open={open}
        modal={!container}
        onOpenChange={(next) => (next ? undefined : onDismiss())}
    >
        <DialogContent
            container={container}
            contained={!!container}
            className="max-w-sm gap-4 text-xs"
            showCloseButton={showCloseButton}
        >
            {children}
        </DialogContent>
    </Dialog>
)

/** The button pressed; the caller turns it into a stored level. */
export type AccessAnswer = "allow" | "readOnly" | "deny"

export interface AccessQuestionProps {
    open: boolean
    /** App name from the manifest, else the folder name. */
    appName: string
    /** App dir as presented to the user. */
    dir: string
    /** What to ask: reading, changing (after a read), or both at once (the manifest declares it). */
    need: "read" | "write" | "read-write"
    /** The manifest asks for write but edits are off here: say so under the read question. */
    writeUnavailable?: boolean
    /** A positioned pane to confine the dialog to; the rest of the page stays live. */
    container?: HTMLElement | null
    onAnswer: (answer: AccessAnswer) => void
    /** Closed without an answer (Esc, ×): nothing is stored. */
    onCancel: () => void
}

const QUESTION: Record<
    AccessQuestionProps["need"],
    {title: TranslationKeys; reason: TranslationKeys}
> = {
    read: {
        title: "com_entity_drive_access_ask_read",
        reason: "com_entity_drive_access_reason_read",
    },
    write: {
        title: "com_entity_drive_access_ask_write",
        reason: "com_entity_drive_access_reason_write",
    },
    "read-write": {
        title: "com_entity_drive_access_ask_read_write",
        reason: "com_entity_drive_access_reason_read_write",
    },
}

/** "Let X read / change / read and change files in dir?" with the answers that fit. */
export function AccessQuestion({
    open,
    appName,
    dir,
    need,
    writeUnavailable = false,
    container,
    onAnswer,
    onCancel,
}: AccessQuestionProps) {
    const t = useLocalize()
    const {title, reason} = QUESTION[need]
    return (
        <AccessDialog open={open} container={container} onDismiss={onCancel} showCloseButton>
            <DialogHeader className="gap-1">
                <DialogTitle className="pr-6 text-sm">
                    {withFolder(t(title, {name: appName, dir: DIR_SLOT}), dir)}
                </DialogTitle>
                <DialogDescription className="text-xs text-colorTextSecondary">
                    {t(reason)}
                    {need === "read" && writeUnavailable
                        ? ` ${t("com_entity_drive_access_write_unavailable")}`
                        : null}
                </DialogDescription>
            </DialogHeader>

            <p className="m-0 text-xs text-colorTextTertiary">
                {t("com_entity_drive_access_trust_note")}
            </p>

            <DialogFooter className="gap-2">
                <Button variant="outline" size="sm" onClick={() => onAnswer("deny")}>
                    {t("com_entity_drive_access_dont_allow")}
                </Button>
                {need === "read-write" ? (
                    <Button variant="outline" size="sm" onClick={() => onAnswer("readOnly")}>
                        {t("com_entity_drive_access_read_only")}
                    </Button>
                ) : null}
                <Button size="sm" onClick={() => onAnswer("allow")}>
                    {need === "read-write"
                        ? t("com_entity_drive_access_allow_read_write")
                        : t("com_entity_drive_access_allow")}
                </Button>
            </DialogFooter>
        </AccessDialog>
    )
}

export interface GrantSheetProps {
    open: boolean
    /** App name from the manifest, else the folder name. */
    appName: string
    /** App dir as presented to the user. */
    dir: string
    /** The stored level, preselected; null when never answered. */
    current: AppAccess | null
    /** Whether "Read and write files" is offered at all. */
    canWrite: boolean
    /** A positioned pane to confine the dialog to; the rest of the page stays live. */
    container?: HTMLElement | null
    onCancel: () => void
    onSave: (level: AppAccess) => void
}

/** The ⋯ "File access…" setting: Read, Read and write (where edits are allowed) or None. */
export function GrantSheet({
    open,
    appName,
    dir,
    current,
    canWrite,
    container,
    onCancel,
    onSave,
}: GrantSheetProps) {
    const t = useLocalize()
    const [level, setLevel] = useState<AppAccess | null>(current)

    return (
        <AccessDialog
            open={open}
            container={container}
            onDismiss={onCancel}
            showCloseButton={false}
        >
            <DialogHeader className="gap-1">
                <DialogTitle className="text-sm">
                    {t("com_entity_drive_access_title", {name: appName})}
                </DialogTitle>
                <DialogDescription className="text-xs text-colorTextSecondary">
                    {withFolder(t("com_entity_drive_access_desc", {dir: DIR_SLOT}), dir)}
                </DialogDescription>
            </DialogHeader>

            <section aria-label={t("com_entity_drive_file_access")} data-slot="grant-sheet-access">
                <RadioGroup
                    value={level ?? ""}
                    onValueChange={(v) => setLevel(v as AppAccess)}
                    className="flex flex-col gap-2"
                >
                    <label className="flex cursor-pointer items-center gap-2">
                        <RadioGroupItem value="read" />
                        {t("com_entity_drive_access_read_files")}
                    </label>
                    {canWrite ? (
                        <label className="flex cursor-pointer items-center gap-2">
                            <RadioGroupItem value="read-write" />
                            {t("com_entity_drive_access_read_write_files")}
                        </label>
                    ) : null}
                    <label className="flex cursor-pointer items-center gap-2">
                        <RadioGroupItem value="none" />
                        {t("com_ui_none")}
                    </label>
                </RadioGroup>
            </section>

            <p className="m-0 text-xs text-colorTextTertiary">
                {t("com_entity_drive_access_trust_note")}
            </p>

            <DialogFooter className="gap-2">
                <Button variant="outline" size="sm" onClick={onCancel}>
                    {t("com_ui_cancel")}
                </Button>
                <Button size="sm" disabled={level === null} onClick={() => level && onSave(level)}>
                    {t("com_ui_save")}
                </Button>
            </DialogFooter>
        </AccessDialog>
    )
}
